// Stock cash-outs with no items yet (F-CTRL-N75): cash was taken from the
// till for stock, but nobody has said what it bought, so the stock never
// arrived. Listed across days so a sealed day's cash-outs can still be done.
// Admin and manager only (the day-ops routes).
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PackagePlus } from 'lucide-react'
import apiClient from '@/api/client'
import type { CashDrawerMovement } from '@/types'
import { Button } from '@/components/ui/button'
import { formatMoney } from '@/lib/currency'
import { formatDateTimeHuman } from '@/lib/utils'
import { getStoredUserRole } from '@/lib/staff-roles'
import { toastHelpers } from '@/lib/toast-helpers'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import { StockCashOutItemsDialog } from './StockCashOutItemsDialog'

export function StockCashOutsBanner() {
  const role = getStoredUserRole()
  const allowed = role === 'admin' || role === 'manager'
  const { data } = useQuery({
    queryKey: ['stock-cash-outs', 'needing-items'],
    queryFn: () => apiClient.getStockCashOutsNeedingItems(),
    enabled: allowed,
    staleTime: 30_000,
  })
  const [adding, setAdding] = useState<CashDrawerMovement | null>(null)
  const list = data?.data ?? []
  if (!allowed || list.length === 0) return null
  return (
    <section className="mb-4 space-y-2 rounded-lg border border-warning-border bg-warning-surface/40 p-4">
      <div>
        <p className="font-semibold">{BT.needingItems(list.length)}</p>
        <p className="text-sm text-muted-foreground">{BT.needingItemsHelp}</p>
      </div>
      <ul className="divide-y">
        {list.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
            <span className="min-w-[10rem] flex-1">
              <span className="font-medium">{m.reason}</span>
              <span className="text-muted-foreground"> · {formatDateTimeHuman(m.created_at)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}</span>
            </span>
            <span className="font-semibold tabular-nums">{formatMoney(m.amount)}</span>
            <Button size="sm" variant="outline" onClick={() => setAdding(m)}><PackagePlus className="mr-1 h-4 w-4" /> {BT.addItems}</Button>
          </li>
        ))}
      </ul>
      <StockCashOutItemsDialog
        movement={adding}
        onClose={() => setAdding(null)}
        notify={(kind, msg) => (kind === 'success' ? toastHelpers.success(msg) : toastHelpers.error(msg))}
      />
    </section>
  )
}
